import type { NaturalEvent, NaturalEventEnvelope } from '@/adapters/primitive/_contract/natural/event';
import type { NaturalReceiverInteraction } from '@/adapters/primitive/_contract/natural/location';
import type {
    NaturalBandState,
    NaturalMachineState,
    NaturalMachineWheel,
    NaturalTrace,
    NaturalTracePoint,
} from '@/adapters/primitive/_contract/natural/machine';
import {
    normalizeNaturalResultPowers,
    resolveCrossingSchedule,
    toNaturalReceiverSlotOffset,
} from '@/adapters/primitive/_pure/model/natural/columnArithmetic';
import { reduceNaturalEvent } from '@/adapters/primitive/_pure/model/natural/machine';
import { settleNaturalBandState } from '@/adapters/primitive/_pure/model/natural/settlementCells';

function emptyBand(power: number): NaturalBandState {
    return { power, countedSlots: [], overhangSlots: [], marker: null };
}

function cloneWheel(wheel: NaturalMachineWheel): NaturalMachineWheel {
    return {
        ...wheel,
        bands: wheel.bands.map((band) => ({
            ...band,
            countedSlots: [...band.countedSlots],
            overhangSlots: [...band.overhangSlots],
        })),
    };
}

function normalizedBands(args: {
    receiverWheel: NaturalMachineWheel;
    radix: number;
    borrowPower: number;
}): NaturalBandState[] {
    const powers = Array.from(
        { length: Math.max(args.borrowPower + 1, args.receiverWheel.bands.length) },
        (_, power) => power,
    );
    const radixBand = (power: number): NaturalBandState => ({
        power,
        countedSlots: Array.from({ length: args.radix }, (_, slot) => slot),
        overhangSlots: [],
        marker: null,
    });
    const bands = powers.map((power) => {
        const heldBand = args.receiverWheel.bands.find((band) => band.power === power) ?? emptyBand(power);
        if (power < args.borrowPower) {
            return settleNaturalBandState({
                radix: args.radix,
                heldBand: emptyBand(power),
                incomingBand: radixBand(power),
                slotOffset: 0,
            });
        }
        if (power === args.borrowPower) {
            const slotOffset = toNaturalReceiverSlotOffset({
                heldDigit: heldBand.countedSlots.length,
                incomingDigit: 1,
                interactionKind: 'subtractive',
            });
            return settleNaturalBandState({
                radix: args.radix,
                heldBand,
                incomingBand: { power, countedSlots: [1], overhangSlots: [], marker: null },
                slotOffset,
            });
        }
        return heldBand;
    });
    const normalization = normalizeNaturalResultPowers({
        powers,
        countedDigitByPower: Object.fromEntries(bands.map((band) => [band.power, band.countedSlots.length])),
    });
    const retained = new Set(normalization.powers);
    return bands.filter((band) => retained.has(band.power));
}

export function deriveNaturalDecrementTrace(args: {
    receiverWheel: NaturalMachineWheel;
    radix: number;
    receiverInteraction?: NaturalReceiverInteraction;
    unitId?: string;
}): NaturalTrace {
    const receiverInteraction = args.receiverInteraction ?? {
        receiver: {
            wheelRole: args.receiverWheel.role,
            wheelId: args.receiverWheel.wheelId,
            power: 0,
        },
        interactionKind: 'subtractive' as const,
    };
    if (receiverInteraction.interactionKind !== 'subtractive') {
        throw new Error('Natural decrement requires a subtractive receiver interaction.');
    }
    const firstCountedBand = [...args.receiverWheel.bands]
        .sort((left, right) => left.power - right.power)
        .find((band) => band.countedSlots.length > 0);
    if (firstCountedBand === undefined) {
        throw new RangeError('Natural zero cannot be decremented.');
    }

    const unitId = args.unitId ?? 'decrement-unit';
    const initialState: NaturalMachineState = {
        operation: 'multiplication',
        radix: args.radix,
        nextSequence: 0,
        status: 'running',
        wheels: [cloneWheel(args.receiverWheel)],
        operandWheelTransfer: null,
        contributionTraveller: null,
        assembly: null,
        composite: null,
        multiplication: {
            unit: null,
            borrowAssembly: null,
        },
    };
    const points: NaturalTracePoint[] = [];
    let state = initialState;
    const emit = (event: NaturalEvent, causedBySequence: number | null): NaturalEventEnvelope => {
        const envelope = { sequence: state.nextSequence, causedBySequence, event };
        const previousState = state;
        state = { ...reduceNaturalEvent(state, event), nextSequence: state.nextSequence + 1 };
        points.push({ previousState, event: envelope, nextState: state });
        return envelope;
    };
    const near = { receiverRole: receiverInteraction.receiver.wheelRole, locationKind: 'standoff-near' as const };
    const far = { receiverRole: receiverInteraction.receiver.wheelRole, locationKind: 'standoff-far' as const };
    const receiver = { receiverRole: receiverInteraction.receiver.wheelRole, locationKind: 'receiver' as const };

    let cause = emit({
        eventKind: 'unit-introduced',
        unitId,
        location: near,
        receiverInteraction,
    }, null);
    const zeroBand = args.receiverWheel.bands.find((band) => band.power === 0) ?? emptyBand(0);
    const zeroOffset = toNaturalReceiverSlotOffset({
        heldDigit: zeroBand.countedSlots.length,
        incomingDigit: 1,
        interactionKind: receiverInteraction.interactionKind,
    });
    cause = emit({
        eventKind: 'unit-aligned',
        unitId,
        slotOffset: zeroOffset,
        location: near,
        receiverInteraction,
    }, cause.sequence);

    for (const crossing of resolveCrossingSchedule({
        operandDigit: 1,
        slotOffset: zeroOffset,
        radix: args.radix,
    })) {
        cause = emit({
            eventKind: 'unit-crossed-zero',
            unitId,
            power: 0,
            digit: crossing.digit,
            receiverInteraction,
        }, cause.sequence);
    }

    let settledBands: NaturalBandState[];
    if (firstCountedBand.power === 0) {
        const settledZero = settleNaturalBandState({
            radix: args.radix,
            heldBand: zeroBand,
            incomingBand: { power: 0, countedSlots: [1], overhangSlots: [], marker: null },
            slotOffset: zeroOffset,
        });
        const bands = args.receiverWheel.bands.map((band) => band.power === 0 ? settledZero : band);
        const normalization = normalizeNaturalResultPowers({
            powers: bands.map((band) => band.power),
            countedDigitByPower: Object.fromEntries(bands.map((band) => [band.power, band.countedSlots.length])),
        });
        const retained = new Set(normalization.powers);
        settledBands = bands.filter((band) => retained.has(band.power));
    } else {
        const assemblyId = `${unitId}-borrow`;
        for (let innerPower = 0; innerPower < firstCountedBand.power; innerPower += 1) {
            cause = emit({
                eventKind: 'borrow-assembly-layer-introduced',
                assemblyId,
                innerPower,
                location: far,
                receiverInteraction,
            }, cause.sequence);
            cause = emit({
                eventKind: 'borrow-assembly-moved',
                assemblyId,
                location: near,
                receiverInteraction,
            }, cause.sequence);
            const outerPower = innerPower + 1;
            const heldBand = args.receiverWheel.bands.find((band) => band.power === outerPower)
                ?? emptyBand(outerPower);
            const outerOffset = toNaturalReceiverSlotOffset({
                heldDigit: heldBand.countedSlots.length,
                incomingDigit: 1,
                interactionKind: receiverInteraction.interactionKind,
            });
            cause = emit({
                eventKind: 'borrow-assembly-outer-unit-aligned',
                assemblyId,
                power: outerPower,
                slotOffset: outerOffset,
                location: near,
                receiverInteraction,
            }, cause.sequence);
            for (const crossing of resolveCrossingSchedule({
                operandDigit: 1,
                slotOffset: outerOffset,
                radix: args.radix,
            })) {
                cause = emit({
                    eventKind: 'borrow-assembly-outer-unit-crossed-zero',
                    assemblyId,
                    power: outerPower,
                    digit: crossing.digit,
                    receiverInteraction,
                }, cause.sequence);
            }
        }
        settledBands = normalizedBands({
            receiverWheel: args.receiverWheel,
            radix: args.radix,
            borrowPower: firstCountedBand.power,
        });
    }

    cause = emit({
        eventKind: 'receiver-bands-settled',
        receiverInteraction,
        bands: settledBands,
    }, cause.sequence);
    emit({ eventKind: 'unit-decrement-completed', unitId }, cause.sequence);

    return { initialState, points };
}